import { colors, COLORS } from '@constants/colors';
import { useTheme } from '@mui/material';
import { ScreenSizes } from '@interfaces';

export const useStyles = () => {
  const { breakpoints } = useTheme();
  const MODAL_WIDTH = 760;
  const MODAL_MAX_WIDTH_MOBILE = 760;

  return {
    modalRoot: {
      '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
      },
    },

    container: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: MODAL_WIDTH,
      background:
        'linear-gradient(135deg, rgba(35, 38, 47, 0.98) 0%, rgba(18, 18, 22, 0.98) 100%)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      maxHeight: '80vh',
      overflow: 'hidden',
      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        width: '92%',
        maxWidth: MODAL_MAX_WIDTH_MOBILE,
      },
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: '18px 20px 12px',
    },
    headerLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexShrink: 0,
    },
    title: {
      color: COLORS.WHITE,
      fontSize: '16px',
      fontWeight: 600,
    },
    subtitle: {
      color: colors.white_50,
      fontSize: '12px',
      fontWeight: 400,
    },
    closeButton: {
      color: colors.white_60,
      padding: '4px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
    createHeaderButton: {
      color: COLORS.WHITE,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      textTransform: 'none',
      padding: '6px 14px',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.4)',
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
      },
    },
    searchWrapper: {
      padding: '0 16px 12px',
    },
    searchField: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(25, 25, 30, 0.6)',
        borderRadius: '8px',
        color: COLORS.WHITE,
        fontSize: '14px',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.08)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
        '&.Mui-focused fieldset': { borderColor: COLORS.MYTAVERSE_BLUE },
      },
      '& .MuiOutlinedInput-input': {
        padding: '8px 10px',
        '&::placeholder': { color: colors.white_40, opacity: 1 },
      },
    },
    searchIcon: {
      fontSize: 18,
      color: colors.white_40,
    },
    content: {
      padding: '0 16px 12px',
      overflowY: 'auto',
      maxHeight: 'calc(80vh - 180px)',
    },
    stateBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      padding: '32px 16px',
    },
    stateText: {
      color: 'rgba(255,255,255,0.6)',
      fontSize: 14,
      textAlign: 'center',
    },
    cardGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'rgba(25, 25, 30, 0.7)',
      borderRadius: '10px',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      cursor: 'pointer',
      overflow: 'hidden',
      transition: 'border-color 0.2s ease',
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.18)',
      },
    },
    cardActive: {
      borderColor: COLORS.GREEN,
      borderWidth: '1.5px',
      '&:hover': {
        borderColor: COLORS.GREEN,
      },
    },
    cardPalette: {
      display: 'flex',
      height: '72px',
      position: 'relative',
      overflow: 'hidden',
      gap: '1px',
      backgroundColor: 'rgba(0,0,0,0.35)',
    },
    colorStripe: {
      flex: 1,
      height: '100%',
    },
    palettePlaceholder: {
      flex: 1,
      height: '100%',
      background:
        'repeating-linear-gradient(45deg, rgba(58,58,70,1) 0px, rgba(58,58,70,1) 8px, rgba(46,46,58,1) 8px, rgba(46,46,58,1) 16px)',
    },
    liveBadge: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      backgroundColor: 'rgba(18, 18, 22, 0.82)',
      backdropFilter: 'blur(6px)',
      borderRadius: '20px',
      padding: '3px 10px 3px 7px',
    },
    liveDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      backgroundColor: COLORS.GREEN,
      flexShrink: 0,
    },
    liveBadgeText: {
      color: COLORS.WHITE,
      fontSize: '12px',
      fontWeight: 500,
    },
    cardBody: {
      display: 'flex',
      flexDirection: 'column',
      padding: '10px 12px 8px',
      gap: '2px',
      flex: 1,
    },
    cardName: {
      color: COLORS.WHITE,
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    cardMeta: {
      color: colors.white_50,
      fontSize: '12px',
      fontWeight: 400,
    },
    cardFooterRow: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginTop: '6px',
    },
    activeLabel: {
      color: COLORS.GREEN,
      fontSize: '12px',
      fontWeight: 500,
    },
    activateButton: {
      color: colors.white_60,
      fontSize: '12px',
      fontWeight: 400,
      textTransform: 'none',
      padding: '2px 6px',
      minWidth: 0,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        color: COLORS.WHITE,
      },
    },
    moreButton: {
      width: 28,
      height: 28,
      color: colors.white_40,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: colors.white_60,
      },
    },
    menuPaper: {
      backgroundColor: 'rgba(25, 25, 30, 0.97)',
      backdropFilter: 'blur(12px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      minWidth: 160,
    },
    menuItem: {
      color: COLORS.WHITE,
      fontSize: '13px',
      gap: '10px',
      padding: '8px 14px',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
      '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' },
    },
    menuItemIcon: {
      fontSize: 17,
      color: 'inherit',
    },
    currentLabel: {
      color: colors.white_50,
      fontSize: '14px',
      fontWeight: 400,
    },
    footer: {
      padding: '12px 16px 16px',
    },
    leaveButton: {
      color: COLORS.WHITE,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      textTransform: 'none',
      padding: '10px 24px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.4)',
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
      },
    },

    createContainer: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: MODAL_WIDTH,
      background:
        'linear-gradient(135deg, rgba(35, 38, 47, 0.98) 0%, rgba(18, 18, 22, 0.98) 100%)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        width: '95%',
        maxWidth: MODAL_MAX_WIDTH_MOBILE,
      },
    },
    createHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px 12px',
    },
    createTitle: {
      color: COLORS.WHITE,
      fontSize: '16px',
      fontWeight: 500,
    },
    cardsContainer: {
      display: 'flex',
      gap: '12px',
      padding: '0 20px 20px',
      [breakpoints.down(ScreenSizes.Mobile)]: {
        flexDirection: 'column',
      },
    },
    cardPrimary: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '24px 20px',
      backgroundColor: COLORS.MYTAVERSE_BLUE,
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#3A46C1',
        transform: 'translateY(-2px)',
      },
    },
    cardSecondary: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '24px 20px',
      backgroundColor: 'rgba(35, 35, 42, 0.9)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(45, 45, 52, 0.9)',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-2px)',
      },
    },
    cardSecondaryDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
      '&:hover': {
        transform: 'none',
      },
    },
    cardIconWrapper: {
      width: 48,
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderRadius: '12px',
      marginBottom: '12px',
    },
    cardIconWrapperSecondary: {
      width: 48,
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      borderRadius: '12px',
      marginBottom: '12px',
    },
    cardIcon: {
      color: COLORS.WHITE,
      fontSize: 24,
    },
    cardTitle: {
      color: COLORS.WHITE,
      fontSize: '14px',
      fontWeight: 600,
      marginBottom: '6px',
      textAlign: 'center',
    },
    cardDescription: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '12px',
      fontWeight: 400,
      textAlign: 'center',
      lineHeight: 1.4,
    },
    createFooter: {
      padding: '0 20px 20px',
    },
    cancelButtonFull: {
      width: '100%',
      color: COLORS.WHITE,
      backgroundColor: 'rgba(50, 50, 58, 0.9)',
      textTransform: 'none',
      padding: '12px 20px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'rgba(60, 60, 68, 0.9)',
      },
    },

    nameContainer: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: MODAL_WIDTH,
      background:
        'linear-gradient(135deg, rgba(35, 38, 47, 0.98) 0%, rgba(18, 18, 22, 0.98) 100%)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        width: '90%',
        maxWidth: MODAL_MAX_WIDTH_MOBILE,
      },
    },
    nameHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px 12px',
    },
    nameHeaderLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    backButton: {
      color: colors.white_60,
      padding: '4px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
    nameTitle: {
      color: COLORS.WHITE,
      fontSize: '16px',
      fontWeight: 500,
    },
    nameContent: {
      padding: '0 20px 20px',
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(25, 25, 30, 0.8)',
        borderRadius: '8px',
        color: COLORS.WHITE,
        fontSize: '14px',
        '& fieldset': {
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&:hover fieldset': {
          borderColor: 'rgba(255, 255, 255, 0.2)',
        },
        '&.Mui-focused fieldset': {
          borderColor: COLORS.MYTAVERSE_BLUE,
        },
      },
      '& .MuiOutlinedInput-input': {
        padding: '12px 14px',
        '&::placeholder': {
          color: colors.white_40,
          opacity: 1,
        },
      },
    },
    characterCount: {
      color: colors.white_40,
      fontSize: '12px',
      textAlign: 'right',
      marginTop: '6px',
    },
    nameFooter: {
      display: 'flex',
      gap: '12px',
      padding: '0 20px 20px',
    },
    cancelButton: {
      flex: 1,
      color: COLORS.WHITE,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      textTransform: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.4)',
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
      },
    },
    continueButton: {
      flex: 1,
      backgroundColor: COLORS.MYTAVERSE_BLUE,
      color: COLORS.WHITE,
      textTransform: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: '#3A46C1',
      },
      '&.Mui-disabled': {
        backgroundColor: 'rgba(69, 84, 229, 0.5)',
        color: 'rgba(255, 255, 255, 0.5)',
      },
    },

    selectContainer: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: MODAL_WIDTH,
      background:
        'linear-gradient(135deg, rgba(35, 38, 47, 0.98) 0%, rgba(18, 18, 22, 0.98) 100%)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      maxHeight: '80vh',
      overflow: 'hidden',
      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        width: '90%',
        maxWidth: MODAL_MAX_WIDTH_MOBILE,
      },
    },
    selectHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px 12px',
    },
    selectTitle: {
      color: COLORS.WHITE,
      fontSize: '16px',
      fontWeight: 500,
    },
    selectContent: {
      padding: '0 12px 12px',
      overflowY: 'auto',
      maxHeight: 'calc(80vh - 140px)',
    },
    selectList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    selectItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 16px',
      backgroundColor: 'rgba(25, 25, 30, 0.6)',
      borderRadius: '8px',
      cursor: 'pointer',
      border: '1px solid transparent',
      transition: 'all 0.2s ease',
      minHeight: '44px',
      '&:hover': {
        backgroundColor: 'rgba(35, 35, 40, 0.8)',
      },
    },
    selectItemActive: {
      borderColor: COLORS.MYTAVERSE_BLUE,
      backgroundColor: 'rgba(69, 84, 229, 0.1)',
    },
    selectItemName: {
      color: COLORS.WHITE,
      fontSize: '14px',
      fontWeight: 400,
      flex: 1,
    },
    radio: {
      color: colors.white_30,
      padding: '4px',
      '&.Mui-checked': {
        color: COLORS.MYTAVERSE_BLUE,
      },
    },
    selectFooter: {
      display: 'flex',
      gap: '12px',
      padding: '12px 12px 16px',
    },
    useSelectedButton: {
      flex: 1,
      backgroundColor: COLORS.MYTAVERSE_BLUE,
      color: COLORS.WHITE,
      textTransform: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: '#3A46C1',
      },
      '&.Mui-disabled': {
        backgroundColor: 'rgba(69, 84, 229, 0.5)',
        color: 'rgba(255, 255, 255, 0.5)',
      },
    },

    editAreaContainer: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: MODAL_WIDTH,
      background:
        'linear-gradient(135deg, rgba(35, 38, 47, 0.98) 0%, rgba(18, 18, 22, 0.98) 100%)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      maxHeight: '85vh',
      overflow: 'hidden',
      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        width: '92%',
        maxWidth: MODAL_MAX_WIDTH_MOBILE,
      },
    },
    editAreaHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: '20px 24px 12px',
    },
    editAreaHeaderLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      minWidth: 0,
    },
    editAreaTitle: {
      color: COLORS.WHITE,
      fontSize: '17px',
      fontWeight: 600,
    },
    editAreaSubtitle: {
      color: colors.white_50,
      fontSize: '12px',
      fontWeight: 400,
    },
    editAreaGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px',
      padding: '4px 24px 16px',
      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        gridTemplateColumns: '1fr',
      },
    },
    editAreaTile: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '18px 16px',
      borderRadius: '10px',
      backgroundColor: 'rgba(25, 25, 30, 0.7)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      cursor: 'pointer',
      transition: 'all 0.18s ease',
      minHeight: 132,
      '&:hover': {
        borderColor: COLORS.MYTAVERSE_BLUE,
        backgroundColor: 'rgba(69, 84, 229, 0.08)',
        transform: 'translateY(-1px)',
      },
    },
    editAreaTileIcon: {
      width: 36,
      height: 36,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      color: COLORS.WHITE,
      '& svg': {
        fontSize: 20,
      },
    },
    editAreaTileTitle: {
      color: COLORS.WHITE,
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: 1.25,
    },
    editAreaTileDescription: {
      color: colors.white_50,
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    editAreaComingSoonSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '0 24px 16px',
    },
    editAreaComingSoonHeader: {
      color: colors.white_50,
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    },
    editAreaComingSoonGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        gridTemplateColumns: '1fr',
      },
    },
    editAreaTileDisabled: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 14px',
      borderRadius: '10px',
      backgroundColor: 'rgba(25, 25, 30, 0.45)',
      border: '1px dashed rgba(255, 255, 255, 0.08)',
      cursor: 'not-allowed',
      opacity: 0.7,
    },
    editAreaTileDisabledBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      minWidth: 0,
    },
    editAreaTileIconDisabled: {
      width: 32,
      height: 32,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
      color: colors.white_40,
      flexShrink: 0,
      '& svg': {
        fontSize: 18,
      },
    },
    editAreaTileTitleDisabled: {
      color: colors.white_60,
      fontSize: '13px',
      fontWeight: 600,
    },
    editAreaTileDescriptionDisabled: {
      color: colors.white_40,
      fontSize: '11px',
      fontWeight: 400,
    },
    editAreaFooter: {
      padding: '0 24px 20px',
    },
  };
};
